import React, { useCallback, useEffect, useState } from "react";
import type { CreatedInvitation, InvitationSummary, Tenant } from "../types";
import type { AdminUser } from "../authApi";
import {
  fetchAdminUsers,
  fetchAdminTenants,
  createUser,
  createTenant,
  assignUserToTenant,
  fetchInvitations,
} from "../authApi";
import { useAuth } from "../AuthContext/AuthContext";
import InviteUserForm from "./InviteUserForm/InviteUserForm";
import InvitationsList from "./InvitationsList/InvitationsList";
import SendTestEmailCard from "./SendTestEmailCard/SendTestEmailCard";
import "./AdminPage.css";

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [invitations, setInvitations] = useState<InvitationSummary[]>([]);
  const [lastResult, setLastResult] = useState<CreatedInvitation | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create user form
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newIsAdmin, setNewIsAdmin] = useState(false);

  // Create tenant form
  const [newTenantName, setNewTenantName] = useState("");
  const [newTenantSlug, setNewTenantSlug] = useState("");

  // Assign form
  const [assignUserId, setAssignUserId] = useState("");
  const [assignTenantId, setAssignTenantId] = useState("");
  const [assignRole, setAssignRole] = useState("member");

  const reload = useCallback(async () => {
    try {
      const [u, t, inv] = await Promise.all([
        fetchAdminUsers(),
        fetchAdminTenants(),
        fetchInvitations(),
      ]);
      setUsers(u);
      setTenants(t);
      setInvitations(inv);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load admin data");
    }
  }, []);

  const handleInvited = useCallback(
    (created: CreatedInvitation) => {
      setLastResult(created);
      setCopied(false);
      reload();
    },
    [reload]
  );

  const handleCopyLink = useCallback(async () => {
    if (!lastResult) return;
    await navigator.clipboard.writeText(lastResult.accept_url);
    setCopied(true);
  }, [lastResult]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createUser({
        email: newEmail,
        name: newName,
        password: newPassword,
        is_admin: newIsAdmin,
      });
      setNewEmail("");
      setNewName("");
      setNewPassword("");
      setNewIsAdmin(false);
      reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to create user");
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createTenant({
        display_name: newTenantName,
        slug: newTenantSlug || undefined,
      });
      setNewTenantName("");
      setNewTenantSlug("");
      reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to create tenant");
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await assignUserToTenant({
        user_id: Number(assignUserId),
        tenant_id: Number(assignTenantId),
        role: assignRole,
      });
      reload();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to assign user");
    }
  };

  return (
    <div className="admin-page">
      <h2 className="admin-heading">Admin Panel</h2>

      {error && <div className="admin-error">{error}</div>}

      {/* Users */}
      <section className="admin-section">
        <h3>Users</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Admin</th>
              <th>Tenants</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.is_admin ? "Yes" : "No"}</td>
                <td>
                  {u.tenants
                    .map((t) => `${t.display_name} (${t.role})`)
                    .join(", ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <form className="admin-form" onSubmit={handleCreateUser}>
          <h4>Create User</h4>
          <input
            placeholder="Email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
          />
          <input
            placeholder="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <input
            placeholder="Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={newIsAdmin}
              onChange={(e) => setNewIsAdmin(e.target.checked)}
            />
            Admin
          </label>
          <button type="submit">Create</button>
        </form>
      </section>

      {/* Tenants */}
      <section className="admin-section">
        <h3>Tenants</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Display Name</th>
              <th>Slug</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id}>
                <td>{t.id}</td>
                <td>{t.display_name}</td>
                <td>{t.slug}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <form className="admin-form" onSubmit={handleCreateTenant}>
          <h4>Create Tenant</h4>
          <input
            placeholder="Display Name"
            value={newTenantName}
            onChange={(e) => setNewTenantName(e.target.value)}
            required
          />
          <input
            placeholder="Slug (auto-generated if blank)"
            value={newTenantSlug}
            onChange={(e) => setNewTenantSlug(e.target.value)}
          />
          <button type="submit">Create</button>
        </form>
      </section>

      {/* Assignments */}
      <section className="admin-section">
        <h3>Assign User to Tenant</h3>
        <form className="admin-form" onSubmit={handleAssign}>
          <select
            value={assignUserId}
            onChange={(e) => setAssignUserId(e.target.value)}
            required
          >
            <option value="">Select user...</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
          <select
            value={assignTenantId}
            onChange={(e) => setAssignTenantId(e.target.value)}
            required
          >
            <option value="">Select tenant...</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name}
              </option>
            ))}
          </select>
          <select
            value={assignRole}
            onChange={(e) => setAssignRole(e.target.value)}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit">Assign</button>
        </form>
      </section>

      {/* Invitations */}
      <section className="admin-section">
        <h3>Invitations</h3>

        {lastResult && (
          <div className="invite-notice">
            <div className="invite-notice-status">
              {lastResult.email_result.sent
                ? `Email sent to ${lastResult.email}.`
                : lastResult.email_result.error
                ? `Email failed (${lastResult.email_result.error}). Share this link manually:`
                : `Email not configured — share this link manually:`}
            </div>
            <div className="invite-notice-link">
              <code>{lastResult.accept_url}</code>
              <button type="button" onClick={handleCopyLink}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}

        <InviteUserForm
          tenants={tenants}
          canGrantPlatformAdmin={!!user?.is_admin}
          onInvited={handleInvited}
        />

        <div className="invite-list-wrap">
          <InvitationsList
            invitations={invitations}
            onChanged={reload}
            onResent={handleInvited}
          />
        </div>
      </section>

      <SendTestEmailCard users={users} />
    </div>
  );
};

export default AdminPage;
